// src/features/system/iam-admin/hooks/useIamAdmin.ts
// ────────────────────────────────────────────────────────────────────────────
// React Query hooks for the IAM membership admin surface.
//
// Encapsulates: fetch + cache + mutations + invalidation. Components stay
// pure renderers. Hook-owns-all-state pattern per workspace conventions.
// ────────────────────────────────────────────────────────────────────────────
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IAM_ADMIN_KEYS,
  listUsersForPicker,
  listTenantsForPicker,
  fetchUserMemberships,
  fetchTenantMembers,
  assignMembership,
  updateMembership,
  retireMembership,
  type AssignMembershipInput,
  type UpdateMembershipInput,
  type Membership,
  type MembershipList,
  type MembershipStatus,
  type UserPickerEntry,
  type TenantPickerEntry,
} from '../api/iamAdminApi';

// ── Pickers ──────────────────────────────────────────────────────────────

export function useIamUsers() {
  return useQuery<UserPickerEntry[], Error>({
    queryKey:  IAM_ADMIN_KEYS.users,
    queryFn:   listUsersForPicker,
    staleTime: 30_000,
    gcTime:    5 * 60_000,
    retry:     2,
  });
}

export function useIamTenants() {
  return useQuery<TenantPickerEntry[], Error>({
    queryKey:  IAM_ADMIN_KEYS.tenants,
    queryFn:   listTenantsForPicker,
    staleTime: 60_000,
    gcTime:    10 * 60_000,
    retry:     2,
  });
}

// ── Memberships per user / per tenant ────────────────────────────────────

export function useUserMemberships(userRef: string | null, includeRetired = false) {
  return useQuery<MembershipList, Error>({
    queryKey: IAM_ADMIN_KEYS.userMemberships(userRef ?? '', includeRetired),
    queryFn:  () => fetchUserMemberships(userRef as string, includeRetired),
    enabled:  typeof userRef === 'string' && userRef.length > 0,
    staleTime: 15_000,
    gcTime:    5 * 60_000,
    retry:     1,
  });
}

export function useTenantMembers(tenantRef: string | null, includeRetired = false) {
  return useQuery<MembershipList, Error>({
    queryKey: IAM_ADMIN_KEYS.tenantMembers(tenantRef ?? '', includeRetired),
    queryFn:  () => fetchTenantMembers(tenantRef as string, includeRetired),
    enabled:  typeof tenantRef === 'string' && tenantRef.length > 0,
    staleTime: 15_000,
    gcTime:    5 * 60_000,
    retry:     1,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

/** Invalidate every membership query so caches reload from the source of truth. */
function useInvalidateMembershipQueries() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['iam-admin', 'user-memberships'] });
    qc.invalidateQueries({ queryKey: ['iam-admin', 'tenant-members'] });
  };
}

/**
 * Snapshot every membership-list cache entry so optimistic mutations can
 * roll back on error. Returns a function that restores all snapshots.
 */
function useSnapshotMembershipCaches() {
  const qc = useQueryClient();
  return (): (() => void) => {
    const userSnaps   = qc.getQueriesData<MembershipList>({ queryKey: ['iam-admin', 'user-memberships'] });
    const tenantSnaps = qc.getQueriesData<MembershipList>({ queryKey: ['iam-admin', 'tenant-members'] });
    return () => {
      for (const [key, data] of userSnaps)   qc.setQueryData(key, data);
      for (const [key, data] of tenantSnaps) qc.setQueryData(key, data);
    };
  };
}

/** Apply a per-membership patch across every cached membership list. */
function applyPatchToCaches(
  qc: ReturnType<typeof useQueryClient>,
  predicate: (m: Membership) => boolean,
  patch: (m: Membership) => Membership,
) {
  const updateList = (key: readonly unknown[]) => {
    qc.setQueriesData<MembershipList | undefined>({ queryKey: key as any }, (prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = prev.memberships.map((m) => {
        if (!predicate(m)) return m;
        changed = true;
        return patch(m);
      });
      if (!changed) return prev;
      const activeCount  = next.filter(m => m.status === 'active' && !m.isRetired).length;
      const retiredCount = next.filter(m => m.isRetired).length;
      const defaultCount = next.filter(m => m.isDefaultBusiness && !m.isRetired).length;
      return {
        ...prev,
        memberships: next,
        summary: { total: next.length, activeCount, retiredCount, defaultCount },
      };
    });
  };
  updateList(['iam-admin', 'user-memberships']);
  updateList(['iam-admin', 'tenant-members']);
}

export function useAssignMembership(userRef: string | null) {
  const invalidate = useInvalidateMembershipQueries();
  return useMutation({
    mutationFn: (input: AssignMembershipInput) => {
      if (typeof userRef !== 'string' || userRef.length === 0) {
        return Promise.reject(new Error('userRef required'));
      }
      return assignMembership(userRef, input);
    },
    // Assign mutates an unknown new row → no safe optimistic patch.
    // Invalidate on success to refetch the canonical list.
    onSuccess: invalidate,
  });
}

export function useUpdateMembership() {
  const qc         = useQueryClient();
  const invalidate = useInvalidateMembershipQueries();
  const snapshot   = useSnapshotMembershipCaches();
  return useMutation({
    mutationFn: ({ membershipRef, input }: { membershipRef: string; input: UpdateMembershipInput }) =>
      updateMembership(membershipRef, input),
    onMutate: async ({ membershipRef, input }) => {
      // Cancel in-flight refetches to avoid clobbering our optimistic patch.
      await qc.cancelQueries({ queryKey: ['iam-admin', 'user-memberships'] });
      await qc.cancelQueries({ queryKey: ['iam-admin', 'tenant-members'] });
      const rollback = snapshot();
      applyPatchToCaches(
        qc,
        (m) => m.membershipRef === membershipRef,
        (m) => ({
          ...m,
          roles:             Array.isArray(input.roles)               ? input.roles               : m.roles,
          status:            typeof input.status === 'string'         ? input.status              : m.status,
          isDefaultBusiness: typeof input.is_default_business === 'boolean' ? input.is_default_business : m.isDefaultBusiness,
          roleSummary:       Array.isArray(input.roles)
            ? (input.roles.length ? input.roles.join(', ') : '—')
            : m.roleSummary,
        }),
      );
      return { rollback };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.rollback?.();
    },
    onSettled: invalidate,
  });
}

export function useRetireMembership() {
  const qc         = useQueryClient();
  const invalidate = useInvalidateMembershipQueries();
  const snapshot   = useSnapshotMembershipCaches();
  return useMutation({
    mutationFn: (membershipRef: string) => retireMembership(membershipRef),
    onMutate: async (membershipRef) => {
      await qc.cancelQueries({ queryKey: ['iam-admin', 'user-memberships'] });
      await qc.cancelQueries({ queryKey: ['iam-admin', 'tenant-members'] });
      const rollback = snapshot();
      applyPatchToCaches(
        qc,
        (m) => m.membershipRef === membershipRef,
        (m) => ({ ...m, isRetired: true, status: 'inactive' as MembershipStatus, isDefaultBusiness: false }),
      );
      return { rollback };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.rollback?.();
    },
    onSettled: invalidate,
  });
}
